import { useState, useEffect } from "react";
import React from "react";
import { getApiUrl, API_CONFIG } from "../config/api";

const initialState = {
  name: "",
  company: "",
  email: "",
  phone: "",
  message: "",
};

export const Contact = () => {
  const [{ name, company, email, phone, message }, setState] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [statusMessage, setStatusMessage] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
    }
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Remove all non-digit characters except + at the beginning
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    
    // Check if it starts with + (international) or is a US number
    if (cleanPhone.startsWith('+')) {
      // International format: +1234567890 (10-15 digits after +)
      return /^\+[1-9]\d{9,14}$/.test(cleanPhone);
    } else {
      // US format: 1234567890 (exactly 10 digits)
      return /^[1-9]\d{9}$/.test(cleanPhone);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!company.trim()) {
      newErrors.company = 'Company is required';
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Auto-hide success message after 10 seconds with countdown
  useEffect(() => {
    if (submitStatus === 'success') {
      setCountdown(10);
      
      const countdownTimer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setSubmitStatus(null);
            setStatusMessage('');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownTimer);
    }
  }, [submitStatus]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    setStatusMessage('');

    try {
      // Use the configured API URL
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.CONTACT_FORM), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          company,
          email,
          phone,
          message,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success) {
          setSubmitStatus('success');
          setStatusMessage('Thank you for your message! We will get back to you soon.');
          setState(initialState);
        } else {
          setSubmitStatus('error');
          setStatusMessage('Sorry, there was an error sending your message. Please try again.');
        }
      } else {
        setSubmitStatus('error');
        setStatusMessage('Sorry, there was an error sending your message. Please try again.');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus('error');
      setStatusMessage('Sorry, there was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="contact" className="contact-section">
      <div className="contact-container">
              <div className="section-title">
          <h2>Contact Us</h2>
          <p>Get in touch with our team to learn more about Peak Risk Management</p>
              </div>
        <div className="row">
          <div className="col-md-8">
            <div className="contact-form">
              <form name="sentMessage" validate onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="name" className="required">Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className={`form-control ${errors.name ? 'error' : ''}`}
                        placeholder="Enter your full name"
                        value={name}
                        onChange={handleChange}
                      />
                      {errors.name && (
                        <div className="error-message">{errors.name}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="company" className="required">Company</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        className={`form-control ${errors.company ? 'error' : ''}`}
                        placeholder="Enter your company name"
                        value={company}
                        onChange={handleChange}
                      />
                      {errors.company && (
                        <div className="error-message">{errors.company}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="email" className="required">Email Address</label>
                      <input
                        type="text"
                        id="email"
                        name="email"
                        className={`form-control ${errors.email ? 'error' : ''}`}
                        placeholder="Enter your email address"
                        value={email}
                        onChange={handleChange}
                      />
                      {errors.email && (
                        <div className="error-message">{errors.email}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="phone" className="required">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className={`form-control ${errors.phone ? 'error' : ''}`}
                        placeholder="Enter your phone number"
                        value={phone}
                        onChange={handleChange}
                      />
                      {errors.phone && (
                        <div className="error-message">{errors.phone}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="form-group">
                      <label htmlFor="message" className="required">Message</label>
                  <textarea
                    name="message"
                    id="message"
                    className={`form-control ${errors.message ? 'error' : ''}`}
                    rows="5"
                    placeholder="Tell us how we can help you"
                    value={message}
                    onChange={handleChange}
                  ></textarea>
                  {errors.message && (
                    <div className="error-message">{errors.message}</div>
                  )}
                </div>
                <button 
                  type="submit" 
                  className="contact-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
                
                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="alert alert-success" style={{ 
                    marginTop: '1rem', 
                    padding: '1rem', 
                    backgroundColor: '#d4edda', 
                    color: '#155724', 
                    border: '1px solid #c3e6cb', 
                    borderRadius: '8px'
                  }}>
                    <i className="fa fa-check-circle" style={{ marginRight: '0.5rem' }}></i>
                    {statusMessage}
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="alert alert-error" style={{ 
                    marginTop: '1rem', 
                    padding: '1rem', 
                    backgroundColor: '#f8d7da', 
                    color: '#721c24', 
                    border: '1px solid #f5c6cb', 
                    borderRadius: '8px' 
                  }}>
                    <i className="fa fa-exclamation-circle" style={{ marginRight: '0.5rem' }}></i>
                    {statusMessage}
                  </div>
                )}
              </form>
            </div>
          </div>
          <div className="col-md-4">
            <div className="contact-info-card">
              <div className="contact-info-header">
                <h3>Get in Touch</h3>
                <p>We're here to help you succeed</p>
              </div>
              <div className="contact-info-content">
                <div className="contact-info-item">
                  <div className="contact-info-item-header">
                    <i className="fa fa-envelope"></i>
                    <h3 className="contact-info-item-header-title">Email Us</h3>
                  </div>
                  <p>
                    <a href="mailto:support@peakrmis.com">
                      support@peakrmis.com
                    </a>
              </p>
            </div>
                <div className="contact-info-item">
                  <div className="contact-info-item-header">
                    <i className="fa fa-clock-o"></i>
                    <h3 className="contact-info-item-header-title">Business Hours</h3>
            </div>
                  <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
            </div>
                <div className="contact-info-item">
                  <div className="contact-info-item-header">
                    <i className="fa fa-headphones"></i>
                    <h3 className="contact-info-item-header-title">Support</h3>
          </div>
                  <p>We're here to help you succeed</p>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};
